/**
 * Saved Payment Methods Component
 * Displays and manages saved payment methods (cards, bank accounts)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, Building2, Trash2, Check } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

interface SavedPaymentMethod {
  id: number;
  method_type: 'card' | 'bank_account';
  display_name: string;
  token: string; // Card token from Coinflow (customer.cards[].token)
  is_default: boolean;
  method_details: {
    last4?: string;
    brand?: string;
    type?: string; // Card type (VISA, MASTERCARD, etc.)
    expMonth?: number;
    expYear?: number;
    bankName?: string;
    accountType?: string;
  };
}

interface SavedPaymentMethodsProps {
  onSelect?: (method: SavedPaymentMethod) => void;
  showSelectButton?: boolean;
  refreshKey?: number; // Key to trigger refresh
  showHeading?: boolean; // Whether to show the "Saved Payment Methods" heading
}

export const SavedPaymentMethods: React.FC<SavedPaymentMethodsProps> = ({
  onSelect,
  showSelectButton = false,
  refreshKey = 0,
  showHeading = true, // Default to true for backward compatibility
}) => {
  const [methods, setMethods] = useState<SavedPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<number | null>(null);

  useEffect(() => {
    fetchMethods();
  }, [refreshKey]); // Refresh when refreshKey changes

  const fetchMethods = async () => {
    try {
      const response = await apiClient.get('/payments/methods');
      setMethods(response || []);
    } catch {
      toast.error('Failed to load saved payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (method: SavedPaymentMethod) => {
    if (!confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      // Pass the card token in the request body for Coinflow deletion
      // Since cards are returned directly from Coinflow (not stored in DB),
      // we need to pass the token to delete it
      await apiClient.delete(`/payments/methods/${method.id}`, {
        token: method.token, // Card token from Coinflow customer.cards[].token
      });
      toast.success('Payment method deleted');
      fetchMethods();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete payment method');
    }
  };

  const handleSelect = (method: SavedPaymentMethod) => {
    setSelectedMethod(method.id);
    if (onSelect) {
      onSelect(method);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2 lg:space-y-3">
        <Skeleton className="h-16 lg:h-20 w-full" />
        <Skeleton className="h-16 lg:h-20 w-full" />
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div className="text-center py-4 lg:py-8 text-muted-foreground">
        <p className="text-[10px] lg:text-sm font-medium">No saved payment methods</p>
        <p className="text-[9px] lg:text-sm mt-1 lg:mt-2">Save a payment method during checkout to use it here</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 lg:space-y-3">
      {showHeading && (
        <h3 className="text-[10px] lg:text-lg font-black text-foreground uppercase tracking-wide">Saved Payment Methods</h3>
      )}
      {methods.map((method) => (
        <Card
          key={method.id}
          className={`cursor-pointer transition-all bg-gradient-to-br from-card via-card/80 to-card/60 backdrop-blur-md border border-border hover:border-primary shadow-lg ${
            selectedMethod === method.id
              ? 'border-primary border-2 shadow-xl'
              : ''
          }`}
          onClick={() => showSelectButton && handleSelect(method)}
        >
          <CardContent className="p-2 lg:p-4">
            <div className="flex items-center justify-between gap-1.5 lg:gap-2">
              <div className="flex items-center gap-1.5 lg:gap-3 flex-1 min-w-0">
                {/* Icon */}
                <div className="text-muted-foreground flex-shrink-0">
                  {method.method_type === 'card' ? (
                    <CreditCard className="h-4 w-4 lg:h-6 lg:w-6" />
                  ) : (
                    <Building2 className="h-4 w-4 lg:h-6 lg:w-6" />
                  )}
                </div>

                {/* Method Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 lg:gap-2">
                    <h4 className="font-black text-[10px] lg:text-sm text-foreground truncate">{method.display_name}</h4>
                    {method.is_default && (
                      <span className="text-[9px] lg:text-xs bg-green-500/20 text-green-400 px-1 lg:px-2 py-0.5 rounded-full border border-green-400/30 font-medium flex-shrink-0">
                        Default
                      </span>
                    )}
                  </div>
                  {method.method_type === 'card' && (
                    <p className="text-[9px] lg:text-sm text-muted-foreground truncate">
                      {method.method_details.brand?.toUpperCase() || 'Card'} ••••{' '}
                      {method.method_details.last4}
                      {method.method_details.expMonth &&
                        method.method_details.expYear &&
                        ` • Expires ${method.method_details.expMonth}/${method.method_details.expYear}`}
                    </p>
                  )}
                  {method.method_type === 'bank_account' && (
                    <p className="text-[9px] lg:text-sm text-muted-foreground truncate">
                      {method.method_details.bankName || 'Bank'} ••••{' '}
                      {method.method_details.last4}
                      {method.method_details.accountType && (
                        <span className="capitalize"> • {method.method_details.accountType}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
                {showSelectButton && (
                  <Button
                    size="sm"
                    variant={selectedMethod === method.id ? 'default' : 'outline'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(method);
                    }}
                    className="h-6 lg:h-8 px-1.5 lg:px-3 text-[9px] lg:text-xs"
                  >
                    {selectedMethod === method.id ? (
                      <>
                        <Check className="h-3 w-3 lg:h-4 lg:w-4 mr-0.5 lg:mr-1" />
                        <span className="hidden lg:inline">Selected</span>
                      </>
                    ) : (
                      'Select'
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(method);
                  }}
                  className="h-6 lg:h-8 w-6 lg:w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                >
                  <Trash2 className="h-3 w-3 lg:h-4 lg:w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

